 
import {useState} from "react";
import {Modal, Confirm, Button} from "semantic-ui-react";
import {TextField} from "@fluentui/react";
import {
    Bookmark24Filled,
    Edit20Regular,
    Delete20Regular,
    Checkmark20Regular,
    Dismiss20Regular
} from "@fluentui/react-icons";

import styles from "./SavedPromptsModal.module.css";
import {ISavedPromptData} from "../../types/savedPrompt";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSelectPrompt: (promptText: string) => void;
    onAddPrompt: (promptText: string) => Promise<void>;
    onEditPrompt: (promptId: number, newText: string) => Promise<void>;
    onDeletePrompt: (promptId: number) => Promise<void>;
    savedPrompts: ISavedPromptData[];
}

export const SavedPromptsModal = ({
    isOpen,
    onClose,
    onSelectPrompt,
    onAddPrompt,
    onEditPrompt,
    onDeletePrompt,
    savedPrompts
}: Props) => {
    const [editingPromptId, setEditingPromptId] = useState<number | null>(null);
    const [isAddingNew, setIsAddingNew] = useState<boolean>(false);
    const [promptInput, setPromptInput] = useState<string>("");
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const [error, setError] = useState<string>("");

    const handlePromptClick = (promptText: string) => {
        if (editingPromptId) return; // Don't select while editing
        onSelectPrompt(promptText);
        onClose();
    };

    const handleEdit = (prompt: ISavedPromptData, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingPromptId(prompt.id);
        setPromptInput(prompt.promptText);
        setError("");
    };

    const handleDelete = (promptId: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setDeleteConfirmId(promptId);
    };

    const confirmDelete = async () => {
        if (!deleteConfirmId) return;
        try {
            await onDeletePrompt(deleteConfirmId);
            setDeleteConfirmId(null);
            setError("");
        } catch (err) {
            setError("Failed to delete prompt. Please try again.");
            console.error("Error deleting prompt:", err);
        }
    };

    const handleSave = async () => {
        const trimmedText = promptInput.trim();

        if (!trimmedText) {
            setError("Prompt text cannot be empty.");
            return;
        }

        // Check for duplicates (excluding current prompt if editing)
        const isDuplicate = savedPrompts.some(
            (p) => p.promptText === trimmedText && p.id !== editingPromptId
        );
        if (isDuplicate) {
            setError("This prompt already exists.");
            return;
        }

        try {
            if (isAddingNew) {
                await onAddPrompt(trimmedText);
            } else if (editingPromptId) {
                await onEditPrompt(editingPromptId, trimmedText);
            }
            handleCancel();
        } catch (err) {
            setError("Failed to save prompt. Please try again.");
            console.error("Error saving prompt:", err);
        }
    };

    const handleCancel = () => {
        setEditingPromptId(null);
        setIsAddingNew(false);
        setPromptInput("");
        setError("");
    };

    const handleAddNew = () => {
        setIsAddingNew(true);
        setError("");
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSave();
        } else if (e.key === "Escape") {
            handleCancel();
        }
    };

    return (
        <>
            <Modal
                className="modal-box"
                open={isOpen}
                onClose={onClose}
                closeIcon
                size="small"
            >
                <Modal.Header>
                    <div className={styles.header}>
                        <div className={styles.headerTitle}>
                            <Bookmark24Filled className={styles.headerIcon} />
                            Saved Prompts
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Content>
                    <Button
                        className="btn btn-blue-solid"
                        content="Add New Prompt"
                        icon="plus circle"
                        primary
                        type="button"
                        style={{marginBottom: "1rem"}}
                        onClick={handleAddNew}
                        title="Add new prompt"
                    />

                    {isAddingNew && (
                        <div className={styles.editForm}>
                            <TextField
                                value={promptInput}
                                onChange={(_, newValue) =>
                                    setPromptInput(newValue || "")
                                }
                                onKeyDown={handleKeyPress}
                                placeholder="Enter your new prompt..."
                                autoFocus
                                multiline
                                autoAdjustHeight
                                resizable={false}
                                errorMessage={error}
                                maxLength={1000}
                            />
                            <div className={styles.editActions}>
                                <Button
                                    primary
                                    onClick={handleSave}
                                    content="Save"
                                    size="small"
                                />
                                <Button
                                    onClick={handleCancel}
                                    content="Cancel"
                                    size="small"
                                />
                            </div>
                        </div>
                    )}

                    {savedPrompts.length === 0 && !isAddingNew && (
                        <div className={styles.emptyState}>
                            <p>No saved prompts yet.</p>
                            <p className={styles.emptyStateHint}>
                                Save your favorite prompts for quick access.
                            </p>
                        </div>
                    )}

                    {savedPrompts.length > 0 && (
                        <div className={styles.promptsList}>
                            {savedPrompts.map((prompt) => (
                                <div
                                    key={prompt.id}
                                    className={`${styles.promptItem} ${
                                        editingPromptId === prompt.id
                                            ? styles.promptItemEditing
                                            : ""
                                    }`}
                                >
                                    {editingPromptId === prompt.id ? (
                                        <div className={styles.inlineEditContainer}>
                                            <TextField
                                                value={promptInput}
                                                onChange={(_, newValue) =>
                                                    setPromptInput(newValue || "")
                                                }
                                                onKeyDown={handleKeyPress}
                                                placeholder="Enter prompt text..."
                                                autoFocus
                                                multiline={promptInput.length > 100}
                                                autoAdjustHeight
                                                resizable={false}
                                                errorMessage={error}
                                                className={styles.inlineEditInput}
                                                maxLength={1000}
                                            />
                                            <div
                                                className={styles.inlineEditActions}
                                            >
                                                <button
                                                    className={`${styles.actionButton} ${styles.saveButton}`}
                                                    onClick={handleSave}
                                                    title="Save"
                                                >
                                                    <Checkmark20Regular />
                                                </button>
                                                <button
                                                    className={styles.actionButton}
                                                    onClick={handleCancel}
                                                    title="Cancel"
                                                >
                                                    <Dismiss20Regular />
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div
                                                className={styles.promptContent}
                                                onClick={() =>
                                                    handlePromptClick(
                                                        prompt.promptText
                                                    )
                                                }
                                            >
                                                <Bookmark24Filled
                                                    className={styles.promptIcon}
                                                />
                                                <span className={styles.promptText}>
                                                    {prompt.promptText}
                                                </span>
                                            </div>
                                            <div className={styles.promptActions}>
                                                <button
                                                    className={styles.actionButton}
                                                    onClick={(e) =>
                                                        handleEdit(prompt, e)
                                                    }
                                                    title="Edit prompt"
                                                >
                                                    <Edit20Regular />
                                                </button>
                                                <button
                                                    className={styles.actionButton}
                                                    onClick={(e) =>
                                                        handleDelete(prompt.id, e)
                                                    }
                                                    title="Delete prompt"
                                                >
                                                    <Delete20Regular />
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </Modal.Content>
            </Modal>
            <Confirm
                open={deleteConfirmId !== null}
                onCancel={() => setDeleteConfirmId(null)}
                onConfirm={confirmDelete}
                content="Are you sure you want to delete this saved prompt?"
                confirmButton={{content: "Delete", negative: true}}
                size="mini"
            />
        </>
    );
};
